import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ArrowLeft, Play, FileText, Image, FileDown, Video, Music, Home, User, Sparkles } from "lucide-react";

const typeIcons = {
  audio: Music,
  video: Video,
  text: FileText,
  photo: Image,
  document: FileDown,
};

const typeLabels = {
  audio: "Méditation",
  video: "Vidéo",
  text: "Lecture",
  photo: "Galerie",
  document: "Document",
};

const Section = () => {
  const { slug } = useParams();
  const [section, setSection] = useState(null);
  const [contents, setContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get all sections to find the one with this slug
        const sectionsRes = await axios.get(`${API}/sections`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const currentSection = sectionsRes.data.find(s => s.slug === slug);
        if (currentSection) {
          setSection(currentSection);
          
          // Get contents for this section
          const contentsRes = await axios.get(`${API}/contents/section/${currentSection.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setContents(contentsRes.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [slug, token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] px-6 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="skeleton h-8 w-32 mb-8 rounded-full" />
          <div className="skeleton h-12 w-64 mb-4 rounded-lg" />
          <div className="skeleton h-20 mb-8 rounded-lg" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#3F2A24] mb-4">Section non trouvée</h2>
          <Link to="/sanctuary" className="btn-sacred">
            Retour au sanctuaire
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] pb-24">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="max-w-2xl mx-auto">
          <Link 
            to="/sanctuary" 
            className="inline-flex items-center gap-2 text-[#4B3329] hover:text-[#C5A059] transition-colors mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm tracking-wide">Sanctuaire</span>
          </Link>
        </div>
      </header>

      {/* Section Header */}
      <section className="px-6 py-4">
        <div className="max-w-2xl mx-auto text-center animate-fade-in-up">
          <h1 className="font-serif text-3xl md:text-4xl text-[#3F2A24] mb-4">
            {section.name}
          </h1>
          <p className="text-[#4B3329] leading-relaxed max-w-md mx-auto">
            {section.description}
          </p>
          <div className="divider-gold mt-6" />
        </div>
      </section>

      {/* Contents */}
      <section className="px-6 py-4">
        <div className="max-w-2xl mx-auto">
          {contents.length === 0 ? (
            <div className="empty-state py-16">
              <Sparkles className="w-16 h-16 mx-auto mb-6 text-[#C5A059] opacity-30" strokeWidth={1} />
              <p className="font-serif text-xl text-[#4B3329] mb-2">
                Contenu à venir
              </p>
              <p className="text-[#8C6A4A]">
                Les ressources de cette section seront bientôt disponibles.
              </p>
            </div>
          ) : (
            <div className="space-y-4 stagger-children">
              {contents.map((content) => {
                const IconComponent = typeIcons[content.type] || FileText;
                return (
                  <Link
                    key={content.id}
                    to={`/content/${content.id}`}
                    className="block card-sacred group"
                    data-testid={`content-${content.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Thumbnail or Icon */}
                      {content.thumbnail_url ? (
                        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                          <img 
                            src={content.thumbnail_url} 
                            alt={content.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C5A059]/20 transition-colors">
                          <IconComponent className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
                        </div>
                      )}
                      
                      {/* Content Info */}
                      <div className="flex-1 min-w-0">
                        <div className="content-type-badge">
                          <IconComponent className="w-3 h-3" strokeWidth={1.5} />
                          <span>{typeLabels[content.type] || content.type}</span>
                        </div>
                        <h3 className="font-serif text-lg text-[#3F2A24] truncate group-hover:text-[#C5A059] transition-colors">
                          {content.title}
                        </h3>
                        {content.description && (
                          <p className="text-sm text-[#8C6A4A] truncate">
                            {content.description}
                          </p>
                        )}
                      </div>

                      {/* Play indicator for audio/video */}
                      {(content.type === 'audio' || content.type === 'video') && (
                        <div className="w-10 h-10 rounded-full bg-[#C5A059] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                          <Play className="w-4 h-4 text-[#F9F7F2] ml-0.5" fill="#F9F7F2" />
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="nav-sacred" data-testid="bottom-nav">
        <Link to="/sanctuary" className="nav-item">
          <Home className="w-5 h-5" strokeWidth={1.5} />
          <span>Accueil</span>
        </Link>
        <button onClick={handleLogout} className="nav-item">
          <User className="w-5 h-5" strokeWidth={1.5} />
          <span>Sortir</span>
        </button>
      </nav>
    </div>
  );
};

export default Section;
